import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "content" }
const _hoisted_2 = { class: "header-text" }
const _hoisted_3 = { class: "footer-buttons" }

import { ref, computed, reactive, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useGeneralStore } from '@/pinia'
import { getPoiById, updatePoi, createPoi, getPoiCategoriesForRealm } from '@/services/pois.service'
import { orderAlphabetical } from '@/utils/sort'
import { POI, PoiCategoryDto, RequestPoi, TYPE, POI_CATEGORY } from '@/interfaces/poi.interface'
import { optionType } from '@/design-system/molecules/DropdownStandard.vue'
import confetti from 'canvas-confetti'
import IconChevroneUpMedium from '../assets/icons/IconChevroneUpMedium.vue'
import GeneralSectionAssets from '../molecules/GeneralSectionAssets.vue'
import ButtonPrimary from '../atoms/buttons/ButtonPrimary.vue'
import ButtonOutline from '../atoms/buttons/ButtonOutline.vue'
//import IndividualSectionAssets from '../molecules/IndividualSectionAssets.vue'
//import ProductInformationSectionAssets from '../molecules/ProductInformationSectionAssets.vue'
import ModalBase from '@/components/features/files/ModalBase.vue'
import { formatNumber } from '@/utils/math'

interface EMITS {
  (e: 'closeCreateAssetsModal')
}
type Props = {
  open?: boolean
  icon?: string
}

export default /*@__PURE__*/_defineComponent({
  __name: 'ModalCreateAssets',
  props: {
    open: { type: Boolean, default: false },
    icon: { default: undefined }
  },
  emits: ["closeCreateAssetsModal"],
  setup(__props: any, { emit: __emit }) {


const emits = __emit
const store = useStore()
const piniaStore = useGeneralStore()
const { t } = useI18n()

const isOpen = ref(true)
const generalInformation = ref(true)
//const individualInformation = ref(true)
//const productInformation = ref(false)

const toggleGeneralInformation = () => (generalInformation.value = !generalInformation.value)
//const toggleIndividualInformation = () =>
//  (individualInformation.value = !individualInformation.value)
//const toggleProductInformation = () => (productInformation.value = !productInformation.value)

const isBuildingSelected = computed(
  () => Object.keys(store.state.mapview.selectedBuilding).length > 0
)
const isOutdoorPoi = computed(() => !isBuildingSelected.value)
const isPlanviewShowing = computed<boolean>(() => store.getters.isPlanviewShowing)
const rightClickCoordinate = computed(() => piniaStore.rightClickCoordinate)
const outdoorPoisForSelectedRealEstate = computed(
  () => store.state.mapview.outdoorPoisForSelectedRealEstate
)
const poisForSelectedLevel = computed(() => store.state.planview.poisForSelectedLevel)
const selectedLevel = computed(() => store.state.selectedLevel)
const selectedRealEstate = computed(() => store.state.mapview.selectedRealEstate)

const editMode = computed(
  () =>
    store.state.selectedPoi &&
    rightClickCoordinate.value[0] === 0 &&
    rightClickCoordinate.value[1] === 0
)

const allPoiCategories = ref<PoiCategoryDto[]>([])
const dropdownCats = ref<optionType[]>([])
const dropdownSubCats = ref<optionType[]>([])
const title = ref(t('general__text__category'))
const subtitle = ref(t('general__text__sub-category'))
const rh2000StringValid = ref(true)
const estimatedHeightValid = ref(true)

const generalSectionData = reactive({
  name: editMode.value ? store.state.selectedPoi.name : '',
  description: editMode.value ? store.state.selectedPoi.description : '',
  rh2000String: '',
  estimatedHeight: editMode.value ? store.state.selectedPoi?.estimatedHeight : null
})

const editedPoi = ref<RequestPoi>({
  categoryId: editMode.value ? store.state.selectedPoi?.categoryId : '',
  coreId: '',
  description: '',
  elevation: editMode.value ? store.state.selectedPoi?.elevation : null,
  estimatedHeight: editMode.value ? store.state.selectedPoi?.estimatedHeight : null,
  location: {
    type: 'Point',
    coordinates: editMode.value
      ? [store.state.selectedPoi.longitude, store.state.selectedPoi.latitude]
      : rightClickCoordinate.value
  },
  name: '',
  productId: editMode.value ? store.state.selectedPoi.productId : '',
  properties: {},
  typeId: editMode.value ? store.state.selectedPoi?.typeId : '',
  navVisId: 0,
  navVisLinks: [],
  siteModelEntityId: 0
})

const poiHeightRH2000 = computed<number | undefined>(() => {
  if (editMode.value) {
    return store.state.selectedPoi.elevation
  }
  if (isOutdoorPoi.value) {
    return 0
  }
  if (selectedLevel.value?.location?.coordinates[2] > 0) {
    return selectedLevel.value?.location.coordinates[2]
  }
  return 2
})

function onSelectedCat(categoryId: string): void {
  const selectedCat = allPoiCategories.value?.find((c) => c.id === categoryId)
  if (!selectedCat) {
    dropdownSubCats.value = []
    return
  }
  const types: optionType[] = selectedCat.types.map((type) => ({
    id: type.id,
    name: type.name,
    disabled: false,
    selected: editMode.value && type.id === store.state.selectedPoi?.typeId
  }))
  types.sort(orderAlphabetical)
  dropdownSubCats.value = types
  title.value = selectedCat.name
  editedPoi.value.categoryId = categoryId

  if (types.length) {
    const selectedSubCategory = types.find((subCat) => subCat.selected)
    const defaultSubCategory = types[0]
    if (selectedSubCategory) {
      setSubCategory(selectedSubCategory.id)
    } else {
      setSubCategory(defaultSubCategory.id)
    }
  } else {
    editedPoi.value.typeId = ''
    subtitle.value = t('general__text__sub-category')
  }
}

function setSubCategory(typeId: string) {
  subtitle.value = dropdownSubCats.value.find((subCat) => subCat.id === typeId)?.name ?? ''
  editedPoi.value.typeId = typeId
  dropdownSubCats.value.forEach((sc) => (sc.selected = sc.id === typeId))
}

function updateRH2000String(inputValue: string | number) {
  let normalized = String(inputValue).replace(',', '.')
  const numericValue = Number(normalized)
  if (normalized.length && isNaN(numericValue)) {
    rh2000StringValid.value = false
    return
  }

  rh2000StringValid.value = true
  const formatted = numericValue.toFixed(2).replace('.', ',')

  generalSectionData.rh2000String = formatted
}

function updateEstimatedHeight(inputValue: string | number) {
  let reg = String(inputValue).replace(',', '.')
  if (reg.length && isNaN(Number(reg))) {
    estimatedHeightValid.value = false
    generalSectionData.estimatedHeight = null
    return
  }
  estimatedHeightValid.value = true
  generalSectionData.estimatedHeight = parseFloat(reg)
}

function populateDropdownCats() {
  const poiLayers =
    allPoiCategories.value?.map((cat) => ({
      ...cat,
      disabled: false,
      selected: editMode.value && cat.id === store.state.selectedPoi?.categoryId
    })) || []
  dropdownCats.value = poiLayers.sort(orderAlphabetical)
}

//const handleExternId = (val: string) => console.log('Extern ID:', val)
//const handleInternId = (val: string) => console.log('Intern ID:', val)
//const handleCondition = (val: string) => console.log('Condition:', val)
//const handleValue = (val: string) => console.log('Value:', val)
//const handleOwnership = (val: string) => console.log('Ownership:', val)
//const handleInstallationYear = (val: string) => console.log('Year:', val)

//const handleProduct = (val: string) => console.log('Product:', val)
//const handleProductId = (val: string) => console.log('Product ID:', val)
//const handleBrandOwner = (val: string) => console.log('Brand Owner:', val)
//const handleBrand = (val: string) => console.log('Brand:', val)
//const handleColor = (val: string) => console.log('Color:', val)
//const handleMaterial = (val: string) => console.log('Material:', val)
//const handleIsHeavy = (val: boolean) => console.log('Is Heavy:', val)
function getMatchingCategoryInStore(categoryId: string) {
  const poiCategoriesForSelectedEntity: POI_CATEGORY[] = isOutdoorPoi.value
    ? store.state.mapview.poiCategoriesForSelectedRealEstate
    : store.state.planview.poiCategoriesForSelectedLevel
  const matchingCategoryInStore = poiCategoriesForSelectedEntity.find(
    (category) => category.id === categoryId
  )
  return matchingCategoryInStore
}

function setUpdatedPoiCategory(categoryId: string, typeId: string) {
  const matchingPoiCategoryInStore: POI_CATEGORY | undefined =
    getMatchingCategoryInStore(categoryId)
  if (matchingPoiCategoryInStore) {
    const matchingTypeInStore: TYPE | undefined = matchingPoiCategoryInStore?.types.find(
      (type) => type.id === typeId
    )
    if (!matchingTypeInStore) {
      matchingPoiCategoryInStore.types.push(
        allPoiCategories.value?.flatMap((c) => c.types ?? [])?.find((t) => t.id === typeId) as TYPE
      )
      matchingPoiCategoryInStore.types.sort(orderAlphabetical)
    }
  }
  return matchingPoiCategoryInStore || addPoiCategoryToStore(categoryId, typeId)
}
function addPoiCategoryToStore(categoryId: string, typeId: string): POI_CATEGORY {
  const selectedCategory = allPoiCategories.value?.find((c) => c.id === categoryId)
  const existingPoiCategoriesForSelectedEntity: POI_CATEGORY[] = isOutdoorPoi.value
    ? store.state.mapview.poiCategoriesForSelectedRealEstate
    : store.state.planview.poiCategoriesForSelectedLevel
  let newCategory = selectedCategory as POI_CATEGORY
  newCategory.visible = false
  newCategory.types = newCategory.types.filter((type) => type.id === typeId)
  newCategory.types[0].visible = false
  newCategory.types[0].disabled = false
  const updatedPoiCategoriesForSelectedEntity = [...existingPoiCategoriesForSelectedEntity]
  updatedPoiCategoriesForSelectedEntity.push(newCategory)
  updatedPoiCategoriesForSelectedEntity.sort(orderAlphabetical)
  if (isOutdoorPoi.value) {
    store.commit('mapview/SET_POI_CATEGORIES_FOR_SELECTED_REAL_ESTATE', {
      categories: updatedPoiCategoriesForSelectedEntity
    })
    const filteredMapPoiLayers = [...store.state.mapview.filteredPoiLayers]
    filteredMapPoiLayers.push({
      disabled: false,
      id: newCategory.id,
      name: newCategory.name,
      visible: false,
      types: newCategory.types
    })
    store.commit('mapview/SET_FILTERED_MAP_POI_LAYERS', {
      filteredPoiLayers: filteredMapPoiLayers
    })
  } else {
    store.commit(
      'planview/SET_POI_CATEGORIES_FOR_SELECTED_LEVEL',
      updatedPoiCategoriesForSelectedEntity
    )
  }
  return newCategory
}
function closeModal(cancel?: boolean) {
  if (editMode.value && cancel) {
    // Reset poi position in store if it was moved
    const existingPoisArray = isOutdoorPoi.value
      ? [...outdoorPoisForSelectedRealEstate.value]
      : [...poisForSelectedLevel.value]
    const originalPoi = existingPoisArray.find(
      (poi) => poi.properties.guid === store.state.selectedPoi.guid
    )
    if (
      originalPoi.geometry.coordinates[0] !== originalPoi.properties.longitude ||
      originalPoi.geometry.coordinates[1] !== originalPoi.properties.latitude
    ) {
      let updatedArray: POI[] = []
      existingPoisArray.forEach((poi) => {
        if (poi.properties.guid === originalPoi.properties.guid) {
          poi.geometry.coordinates = [poi.properties.longitude, poi.properties.latitude]
        }
        updatedArray.push(poi)
      })
      isOutdoorPoi.value
        ? store.commit('mapview/SET_OUTDOOR_POI_DATA_FOR_REAL_ESTATE', { pois: updatedArray })
        : store.commit('planview/SET_POIS_FOR_SELECTED_LEVEL', updatedArray)
    }
  }
  piniaStore.setRightClickCoordinate([0, 0])
  emits('closeCreateAssetsModal')
}

function handleMoveButtonClick() {
  store.commit('SET_CURRENT_TOOL', { tool: 'drawpoint' })
}
function updateFilteredPoiLayers(typeId: string, categoryId: string, checked: boolean) {
  isOutdoorPoi.value
    ? store.dispatch('mapview/GET_OUTDOOR_POI_DATA_FOR_REAL_ESTATE', {
        categoryId: categoryId,
        typeId: typeId,
        checked: checked,
        realEstateId: store.getters['mapview/selectedRealEstateId']
      })
    : store.dispatch('planview/getPoisForLevelPerCategory', {
        categoryId: categoryId,
        typeId: typeId,
        checked: checked,
        levelId: store.getters.selectedLevelId
      })
}
async function savePoi() {
  editedPoi.value.name = generalSectionData.name
  editedPoi.value.description = generalSectionData.description
  editedPoi.value.elevation = parseFloat(generalSectionData.rh2000String)
  editedPoi.value.estimatedHeight = generalSectionData.estimatedHeight

  if (!editedPoi.value.name) {
    store.commit('setInfoMessage', { type: 'error', message: t('general__text__missing-name') })
    return
  }
  if (!editedPoi.value.typeId) {
    store.commit('setInfoMessage', {
      type: 'error',
      message: t('general__text__category') + ' ' + t('general__text__missing').toLocaleLowerCase()
    })
    return
  }
  if (!generalSectionData.rh2000String || !rh2000StringValid.value) {
    store.commit('setInfoMessage', {
      type: 'error',
      message:
        t('general__text__height-in-RH') + ' ' + t('general__text__missing').toLocaleLowerCase()
    })
    return
  }

  if (editedPoi.value.location.coordinates.length < 3) {
    editedPoi.value.location.coordinates.push(editedPoi.value.elevation)
  }

  try {
    let result
    if (editMode.value) {
      delete editedPoi.value.siteModelEntityId
      result = await updatePoi(store.state.selectedPoi?.guid, editedPoi.value)
    } else {
      result = await createPoi(editedPoi.value)
    }

    if (result) {
      store.commit('setInfoMessage', {
        type: 'success',
        message: t('general__text__poi-change-may-take-time')
      })

      const poiId = editMode.value ? store.state.selectedPoi?.guid : result.id
      const updatedPoi = await getPoiById(poiId, false)

      if (updatedPoi !== undefined) {
        const updatedPoiCategory = setUpdatedPoiCategory(updatedPoi.categoryId, updatedPoi.typeId)
        const updatedPoiType: TYPE | undefined = updatedPoiCategory.types.find(
          (poiType) => poiType.id === updatedPoi.typeId
        )

        if (!updatedPoiType?.visible) {
          const existingPoisArray = isOutdoorPoi.value
            ? [...outdoorPoisForSelectedRealEstate.value]
            : [...poisForSelectedLevel.value]
          const poiIndex = existingPoisArray.findIndex(
            (poi) => poi.properties.guid === updatedPoi.id
          )
          if (poiIndex > -1) {
            const updatedArray = existingPoisArray.filter(
              (poi) => poi.properties.guid !== updatedPoi.id
            )
            isOutdoorPoi.value
              ? store.commit('mapview/SET_OUTDOOR_POI_DATA_FOR_REAL_ESTATE', { pois: updatedArray })
              : store.commit('planview/SET_POIS_FOR_SELECTED_LEVEL', updatedArray)
          }
          updateFilteredPoiLayers(updatedPoi.typeId, updatedPoi.categoryId, true)
        }

        if (updatedPoiType?.visible) {
          const existingPoisArray = isOutdoorPoi.value
            ? [...outdoorPoisForSelectedRealEstate.value]
            : [...poisForSelectedLevel.value]
          let updatedArray: POI[] = []
          if (editMode.value) {
            updatedArray = existingPoisArray.map((poi) => {
              if (poi.properties.guid === updatedPoi.id) {
                return {
                  ...poi,
                  geometry: {
                    ...poi.geometry,
                    coordinates: [updatedPoi.longitude, updatedPoi.latitude]
                  },
                  properties: {
                    ...poi.properties,
                    ...updatedPoi,
                    color: updatedPoiType?.color
                  }
                }
              }
              return poi
            })
          } else {
            const newPoi: POI = {
              id: updatedPoi.id!,
              geometry: { type: 'Point', coordinates: [updatedPoi.longitude, updatedPoi.latitude] },
              type: 'Feature',
              properties: { ...updatedPoi, color: updatedPoiType?.color }
            }
            updatedArray = [...existingPoisArray, newPoi]
          }
          isOutdoorPoi.value
            ? store.commit('mapview/SET_OUTDOOR_POI_DATA_FOR_REAL_ESTATE', { pois: updatedArray })
            : store.commit('planview/SET_POIS_FOR_SELECTED_LEVEL', updatedArray)
        }
        store.dispatch('CHANGE_POI', { poi: updatedPoi })
        store.commit('SET_CLICKED_ITEM_TYPE', { type: 'poi' })
      }
      if (store.state.oidcStore?.user?.preferred_username?.toLowerCase()?.includes('zynka')) {
        confetti({ particleCount: 400, spread: 300, origin: { y: 0.6 } })
      }

      closeModal()
    }
  } catch (error) {
    console.error(error)
    store.commit('setInfoMessage', {
      type: 'error',
      message: `${t('general__text__something-went-wrong-could-not-save-poi')}`
    })
  }
}
onMounted(async () => {
  editedPoi.value.coreId = editMode.value
    ? store.state.selectedPoi?.coreId
    : isOutdoorPoi.value
    ? selectedRealEstate.value.id
    : selectedLevel.value.id
  editedPoi.value.navVisId =
    Number(
      editMode.value
        ? store.state.selectedPoi?.navVisId
        : isOutdoorPoi.value
        ? selectedRealEstate.value?.navVisId
        : selectedLevel.value?.navVisId
    ) || 0

  allPoiCategories.value =
    (await getPoiCategoriesForRealm(isOutdoorPoi.value ? 'OUTDOOR' : 'INDOOR')) || []
  populateDropdownCats()

  if (editMode.value && store.state.selectedPoi.categoryId) {
    onSelectedCat(store.state.selectedPoi.categoryId)
  }

  if (poiHeightRH2000.value !== undefined && poiHeightRH2000.value !== null) {
    updateRH2000String(formatNumber(poiHeightRH2000.value, 5))
  }
})
watch(
  () => store.state.drawPointFeature,
  (newPoint) => {
    if (newPoint?.geometry) {
      editedPoi.value.location = newPoint.geometry
    }
  },
  { deep: true }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ModalBase, {
    title: _unref(t)('general__text__new-asset'),
    open: isOpen.value,
    icon: _ctx.icon,
    hideDivider: false,
    hideControls: false,
    onClose: closeModal
  }, {
    content: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", {
          class: "sub-header",
          onClick: toggleGeneralInformation
        }, [
          _createElementVNode("div", _hoisted_2, _toDisplayString(_unref(t)('general__text__general-information')), 1),
          _createVNode(IconChevroneUpMedium, {
            class: _normalizeClass(["accordion__arrow-button__icon", { rotated: !generalInformation.value }])
          }, null, 8, ["class"])
        ]),
        (generalInformation.value)
          ? (_openBlock(), _createBlock(GeneralSectionAssets, {
              key: 0,
              name: generalSectionData.name,
              description: generalSectionData.description,
              "rh2000-string": generalSectionData.rh2000String,
              "estimated-height": generalSectionData.estimatedHeight,
              "category-options": dropdownCats.value,
              "sub-category-options": dropdownSubCats.value,
              "category-title": title.value,
              "sub-category-title": subtitle.value,
              "is-outdoor-poi": isOutdoorPoi.value,
              "is-planview-showing": isPlanviewShowing.value,
              "edit-mode": editMode.value,
              title: title.value,
              onUpdateName: _cache[0] || (_cache[0] = ($event: any) => (generalSectionData.name = $event)),
              onUpdateDescription: _cache[1] || (_cache[1] = ($event: any) => (generalSectionData.description = $event)),
              onUpdateRh2000String: updateRH2000String,
              onUpdatEstimatedHeight: updateEstimatedHeight,
              onSelectCategory: onSelectedCat,
              onSelectSubCategory: setSubCategory
            }, null, 8, ["name", "description", "rh2000-string", "estimated-height", "category-options", "sub-category-options", "category-title", "sub-category-title", "is-outdoor-poi", "is-planview-showing", "edit-mode", "title"]))
          : _createCommentVNode("", true)
      ])
    ]),
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_3, [
        (editMode.value)
          ? (_openBlock(), _createBlock(ButtonOutline, {
              key: 0,
              class: "button",
              buttonText: _unref(t)('general__text__move'),
              size: "medium",
              onOnClicked: handleMoveButtonClick
            }, null, 8, ["buttonText"]))
          : _createCommentVNode("", true),
        _createVNode(ButtonOutline, {
          class: "button",
          buttonText: _unref(t)('general__text__cancel'),
          size: "medium",
          onOnClicked: _cache[2] || (_cache[2] = ($event: any) => (closeModal(true)))
        }, null, 8, ["buttonText"]),
        _createVNode(ButtonPrimary, {
          class: "button",
          buttonText: _unref(t)('general__text__create-new-asset'),
          size: "medium",
          onOnClicked: _cache[3] || (_cache[3] = ($event: any) => (savePoi()))
        }, null, 8, ["buttonText"])
      ])
    ]),
    _: 1
  }, 8, ["title", "open", "icon"]))
}
}

})